// Makes the DOM resilient to browser translation tools (Google Translate,
// Microsoft Translator, etc.). Those tools wrap/relocate text nodes inside
// <font> elements; React then tries to removeChild/insertBefore a node that is
// no longer where it expects and throws:
//   "Failed to execute 'removeChild' on 'Node': The node to be removed is not a
//    child of this node."
// which would otherwise crash the whole app. These guards no-op the operation
// (instead of throwing) only in the exact mismatch case. See facebook/react#11538.

export function installDomSafetyGuards(): void {
  if (typeof Node !== 'function' || !Node.prototype) return;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      if (child.parentNode) {
        // The node moved (e.g. into a translation <font> wrapper) — remove it
        // from wherever it actually lives so React's bookkeeping stays sane.
        return child.parentNode.removeChild(child) as T;
      }
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
